import { Briefcase02, DotsHorizontal, Map01 } from "@untitledui/icons";
import type { ReactNode } from "react";
import { IconButton } from "@/components/Atoms/IconButton/IconButton";
import { Label } from "@/components/base/label/label";
import { FeaturedIcon } from "@/components/foundations/featured-icon/featured-icon";
import { cx } from "@/utils/cx";

export const ListItemType = {
	Card: "card",
	List: "list",
} as const;

export type ListItemType = (typeof ListItemType)[keyof typeof ListItemType];

export const ListItemAlignment = {
	Top: "top",
	Middle: "middle",
} as const;

export type ListItemAlignment =
	(typeof ListItemAlignment)[keyof typeof ListItemAlignment];

export const ListItemState = {
	Default: "default",
	Hover: "hover",
	Disabled: "disabled",
	Loading: "loading",
	Error: "error",
	Inactive: "inactive",
} as const;

export type ListItemState = (typeof ListItemState)[keyof typeof ListItemState];

export interface ListItemProps {
	/**
	 * Card (bordered) vs List (borderless dense).
	 * @default "card"
	 */
	type?: ListItemType;
	/**
	 * Vertical alignment of leading / trailing relative to content.
	 * @default "top"
	 */
	alignment?: ListItemAlignment;
	/**
	 * Visual state. `hover` is for Storybook / Figma previews; live hover uses CSS.
	 * `inactive` has no row fill and no row hover.
	 * @default "default"
	 */
	state?: ListItemState;
	/** Primary label. */
	primary?: ReactNode;
	/** Optional overline above the primary label. */
	overline?: ReactNode;
	/**
	 * Show the overline line.
	 * @default true
	 */
	showOverline?: boolean;
	/** Leading slot. Defaults to FeaturedIcon (sm, gray, light). Pass `null` to hide. */
	leading?: ReactNode | null;
	/** Trailing slot. Defaults to overflow IconButton. Pass `null` to hide. */
	trailing?: ReactNode | null;
	/**
	 * Supporting metadata row under primary. Defaults to Label + body text
	 * when unset; pass `null` to hide.
	 */
	supportingElements?: ReactNode | null;
	/** Accessible name for the default trailing overflow control. */
	trailingAriaLabel?: string;
	/** Called when the row is activated (interactive rows only). */
	onPress?: () => void;
	className?: string;
}

const DEFAULT_TRAILING_ARIA_LABEL = "More actions";

/**
 * Full-width list / card row (Figma ListItem 196:1066).
 *
 * Leading, supporting, and trailing are slots. Defaults match the DS:
 * FeaturedIcon, Label + body text, and a link IconButton.
 */
export function ListItem({
	type = "card",
	alignment = "top",
	state = "default",
	primary = "Primary label",
	overline = "Overline",
	showOverline = true,
	leading,
	trailing,
	supportingElements,
	trailingAriaLabel = DEFAULT_TRAILING_ARIA_LABEL,
	onPress,
	className,
}: ListItemProps) {
	const isLoading = state === "loading";
	const isDisabled = state === "disabled";
	const isError = state === "error";
	const isForcedHover = state === "hover";
	const isInactive = state === "inactive";
	const isCard = type === "card";
	const isMiddle = alignment === "middle";

	const resolvedLeading =
		leading === undefined ? (
			<FeaturedIcon
				icon={Briefcase02}
				size="sm"
				color="gray"
				theme="light"
				className="rounded-full! before:rounded-full!"
			/>
		) : (
			leading
		);

	const resolvedTrailing =
		trailing === undefined ? (
			<IconButton
				aria-label={trailingAriaLabel}
				hierarchy="link"
				size="sm"
				shape="round"
				icon={DotsHorizontal}
				isDisabled={isDisabled}
			/>
		) : (
			trailing
		);

	const resolvedSupporting =
		supportingElements === undefined ? (
			<>
				<Label size="sm" type="light" icon={Map01} showIcon>
					Data
				</Label>
				<p className="text-xs font-medium text-tertiary whitespace-nowrap">
					Paragraph or body text
				</p>
			</>
		) : (
			supportingElements
		);

	const interactive =
		Boolean(onPress) && !isDisabled && !isLoading && !isInactive;

	const rootClassName = cx(
		"flex w-full min-h-[44px] items-start gap-(--spacing-xl,16px)",
		!isInactive && "bg-primary",
		isCard
			? "rounded-xl border border-solid p-(--spacing-xl,16px)"
			: "rounded-xl p-(--spacing-md,8px)",
		isCard && !isError && "border-primary",
		isCard && isError && "border-[1.5px] border-error",
		isForcedHover && "bg-primary_hover",
		!isDisabled &&
			!isLoading &&
			!isForcedHover &&
			!isInactive &&
			"hover:bg-primary_hover",
		isDisabled && "opacity-60",
		interactive && "cursor-pointer",
		className,
	);

	const leadingAlignClass = isMiddle
		? "flex items-center self-stretch"
		: "flex items-start justify-center self-stretch";

	const contentAlignClass = isMiddle
		? "flex min-w-0 flex-1 flex-col items-start justify-center overflow-clip"
		: "flex min-w-0 flex-1 flex-col items-start overflow-clip";

	const content = isLoading ? (
		<div
			className="flex min-w-0 flex-1 flex-col gap-(--spacing-sm,6px)"
			aria-hidden
		>
			<div className="h-4 w-[140px] rounded-md bg-tertiary" />
			<div className="h-3 w-full rounded-md bg-tertiary" />
		</div>
	) : (
		<>
			{resolvedLeading != null && (
				<div className={leadingAlignClass}>{resolvedLeading}</div>
			)}

			<div className={contentAlignClass}>
				{showOverline && overline != null && overline !== "" && (
					<p className="w-full truncate font-display text-xs font-extrabold tracking-[0.24px] text-quaternary">
						{overline}
					</p>
				)}
				{primary != null && primary !== "" && (
					<p className="w-full truncate font-display text-sm font-extrabold text-primary">
						{primary}
					</p>
				)}
				{resolvedSupporting != null && (
					<div className="flex w-full items-center gap-(--spacing-2xl,20px)">
						{resolvedSupporting}
					</div>
				)}
			</div>

			{resolvedTrailing != null && (
				<div
					className={cx(
						// overflow-visible so IconButton / menu trigger hover+focus rings are not clipped
						"flex shrink-0 items-center gap-(--spacing-md,8px) self-stretch overflow-visible",
						isForcedHover && "justify-center",
						!isForcedHover && "justify-end",
					)}
				>
					{resolvedTrailing}
				</div>
			)}
		</>
	);

	if (interactive) {
		return (
			<button
				type="button"
				className={cx(rootClassName, "text-left")}
				disabled={isDisabled}
				aria-disabled={isDisabled || undefined}
				aria-busy={isLoading || undefined}
				onClick={() => onPress?.()}
			>
				{content}
			</button>
		);
	}

	return (
		<div
			className={rootClassName}
			aria-disabled={isDisabled || undefined}
			aria-busy={isLoading || undefined}
		>
			{content}
		</div>
	);
}
